import React, { type ImgHTMLAttributes } from "react";
import {
  imageVariants,
  imageSrcSet,
  optimizedImageSrc,
} from "@/lib/imageVariants";

interface ResponsiveImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    "src" | "width" | "height"
  > {
  src?: string;
  alt: string;
  width?: number;
  height?: number;
  /** Optional alternate composition for narrow viewports. */
  mobileSrc?: string;
}

export const CARD_SIZES =
  "(min-width: 1280px) 384px, (min-width: 1024px) calc((100vw - 96px) / 3), (min-width: 640px) calc((100vw - 72px) / 2), calc(100vw - 32px)";
export const HALF_SIZES =
  "(min-width: 1280px) 584px, (min-width: 768px) calc((100vw - 80px) / 2), calc(100vw - 32px)";
export const ARTICLE_SIZES = "(min-width: 1024px) 736px, calc(100vw - 32px)";

/** Native responsive selection works in prerendered HTML, before JavaScript runs. */
export default function ResponsiveImage({
  src,
  alt,
  width,
  height,
  sizes = "(min-width: 1280px) 1200px, calc(100vw - 32px)",
  srcSet,
  mobileSrc,
  loading = "lazy",
  fetchPriority = "auto",
  decoding = "async",
  ...props
}: ResponsiveImageProps) {
  if (!src) return null;
  const image = imageVariants(src);
  const mobile = mobileSrc ? imageVariants(mobileSrc) : undefined;
  const resolvedSizes = loading === "lazy" && image && /(?:^|\s)w-full(?:\s|$)/.test(props.className ?? "") ? `auto, ${sizes}` : sizes;
  const img = (
    <img
      {...props}
      src={image ? optimizedImageSrc(src) : src}
      srcSet={srcSet ?? (image ? imageSrcSet(image) : undefined)}
      alt={alt}
      width={image?.width ?? width}
      height={image?.height ?? height}
      sizes={resolvedSizes}
      loading={loading}
      fetchPriority={fetchPriority}
      decoding={decoding}
    />
  );
  if (!image || srcSet) return img;
  return (
    <picture style={{ display: "contents" }}>
      {mobile?.avif && (
        <source
          media="(max-width: 640px)"
          type="image/avif"
          srcSet={imageSrcSet(mobile, "avif")}
          sizes={resolvedSizes}
          width={mobile.width}
          height={mobile.height}
        />
      )}
      {mobile && (
        <source
          media="(max-width: 640px)"
          type="image/webp"
          srcSet={imageSrcSet(mobile)}
          sizes={resolvedSizes}
          width={mobile.width}
          height={mobile.height}
        />
      )}
      {image.avif && (
        <source
          type="image/avif"
          srcSet={imageSrcSet(image, "avif")}
          sizes={resolvedSizes}
        />
      )}
      {img}
    </picture>
  );
}
